// src/pages/admin/CouponsPage.jsx
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Trash2, ToggleLeft, ToggleRight } from 'lucide-react'
import { adminCouponsApi } from '../../api/services'
import { Card, CardBody, Button, Badge, PageSpinner } from '../../components/common/ui'
import toast from 'react-hot-toast'

const INIT = { code:'', type:'percent', value:'', min_order_amount:'', max_discount:'', per_user_limit:'', total_limit:'', expires_at:'', is_active:true, scope:'global', new_users_only:false }

export default function CouponsPage() {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(INIT)
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.type==='checkbox' ? e.target.checked : e.target.value }))

  const { data, isLoading } = useQuery({ queryKey: ['admin-coupons'], queryFn: () => adminCouponsApi.list() })
  const save = useMutation({ mutationFn: () => editing==='new' ? adminCouponsApi.create(form) : adminCouponsApi.update(editing, form), onSuccess: () => { toast.success('Saved'); qc.invalidateQueries(['admin-coupons']); setEditing(null) }, onError: e => toast.error(e.response?.data?.message || 'Error') })
  const del = useMutation({ mutationFn: id => adminCouponsApi.delete(id), onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries(['admin-coupons']) } })
  const toggle = useMutation({ mutationFn: id => adminCouponsApi.toggle(id), onSuccess: () => qc.invalidateQueries(['admin-coupons']) })

  const coupons = data?.data?.data?.data || []

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-display font-bold">Coupons</h1>
        <Button size="sm" onClick={() => { setEditing('new'); setForm(INIT) }}><Plus size={14} /> New Coupon</Button>
      </div>

      {editing && (
        <Card className="mb-6">
          <CardBody className="space-y-3">
            <div className="grid sm:grid-cols-3 gap-3">
              <input className="input" placeholder="Code *" value={form.code} onChange={set('code')} style={{ textTransform:'uppercase' }} />
              <select className="input" value={form.type} onChange={set('type')}><option value="percent">Percentage %</option><option value="fixed">Fixed ₹</option></select>
              <input className="input" type="number" placeholder="Value *" value={form.value} onChange={set('value')} />
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <input className="input" type="number" placeholder="Min order ₹" value={form.min_order_amount} onChange={set('min_order_amount')} />
              <input className="input" type="number" placeholder="Max discount ₹" value={form.max_discount} onChange={set('max_discount')} />
              <input className="input" type="date" placeholder="Expires at" value={form.expires_at} onChange={set('expires_at')} />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input className="input" type="number" placeholder="Per-user limit" value={form.per_user_limit} onChange={set('per_user_limit')} />
              <input className="input" type="number" placeholder="Total uses limit" value={form.total_limit} onChange={set('total_limit')} />
            </div>
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.is_active} onChange={set('is_active')} /> Active</label>
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.new_users_only} onChange={set('new_users_only')} /> New users only</label>
            </div>
            <div className="flex gap-2">
              <Button loading={save.isPending} onClick={() => save.mutate()}>Save Coupon</Button>
              <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            </div>
          </CardBody>
        </Card>
      )}

      {isLoading && <PageSpinner />}
      <div className="space-y-2">
        {coupons.map(c => (
          <Card key={c.id}>
            <CardBody className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-sm">{c.code}</span>
                  <Badge variant={c.type==='percent'?'blue':'green'}>{c.type==='percent'?`${c.value}%`:`₹${c.value}`} off</Badge>
                  {!c.is_active && <Badge variant="grey">Inactive</Badge>}
                </div>
                <p className="text-xs text-muted">Used: {c.usage_count||0}{c.total_limit?`/${c.total_limit}`:''} times {c.expires_at?`· Expires ${new Date(c.expires_at).toLocaleDateString('en-IN')}`:''}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => toggle.mutate(c.id)} className="p-1.5 text-muted hover:text-pink">{c.is_active?<ToggleRight size={18} className="text-green-500"/>:<ToggleLeft size={18}/>}</button>
                <button onClick={() => { setEditing(c.id); setForm({ ...INIT, ...c }) }} className="p-1.5 text-muted hover:text-pink"><Pencil size={14}/></button>
                <button onClick={() => del.mutate(c.id)} className="p-1.5 text-muted hover:text-red-500"><Trash2 size={14}/></button>
              </div>
            </CardBody>
          </Card>
        ))}
        {!isLoading && coupons.length===0 && <p className="text-center text-muted py-12">No coupons created.</p>}
      </div>
    </div>
  )
}
